import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Command,
  FileSearch,
  Keyboard,
  Loader2,
  Search,
  TerminalSquare,
} from 'lucide-react';
import { axiomTheme } from '../styles/designTokens';
import {
  getOperatorCommandMatches,
  OperatorCommandMatch,
} from '../utils/operatorCommandRegistry';

interface SearchResult {
  id: string | number;
  type: string;
  assetClass?: string;
  title: string;
  subtitle: string;
  metadata?: Record<string, string | number>;
  route?: string;
}

interface QuickSearchProps {
  isOpen: boolean;
  onClose: () => void;
  onShowShortcuts?: () => void;
}

type PaletteItem =
  | { kind: 'command'; id: string; command: OperatorCommandMatch }
  | { kind: 'search'; id: string; result: SearchResult };

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.72);
  backdrop-filter: blur(10px);
  z-index: 9999;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 12vh 1.25rem 2rem;
`;

const Palette = styled(motion.div)`
  width: min(760px, 100%);
  max-height: 78vh;
  overflow: hidden;
  border: 1px solid ${axiomTheme.color.border.default};
  border-radius: ${axiomTheme.radius.hero};
  background:
    linear-gradient(180deg, rgba(15, 23, 42, 0.98), rgba(2, 6, 23, 0.98));
  box-shadow: ${axiomTheme.shadow.drawer};
`;

const SearchHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 1rem 1.1rem;
  border-bottom: 1px solid ${axiomTheme.color.border.subtle};
`;

const HeaderIcon = styled.div`
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${axiomTheme.radius.md};
  border: 1px solid rgba(56, 189, 248, 0.22);
  background: rgba(8, 145, 178, 0.12);
  color: ${axiomTheme.color.tone.cyan};
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  background: transparent;
  border: none;
  outline: none;
  color: ${axiomTheme.color.text.primary};
  font-size: 0.98rem;
  font-weight: 700;

  &::placeholder {
    color: ${axiomTheme.color.text.faint};
    font-weight: 600;
  }
`;

const KeyHint = styled.kbd`
  min-height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.15rem 0.45rem;
  border-radius: ${axiomTheme.radius.xs};
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: rgba(2, 6, 23, 0.58);
  color: ${axiomTheme.color.text.faint};
  font-size: 0.66rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-weight: 800;
`;

const ResultsContainer = styled.div`
  max-height: calc(78vh - 130px);
  overflow-y: auto;
  padding: 0.55rem 0;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.18);
    border-radius: ${axiomTheme.radius.pill};
  }
`;

const ResultSection = styled.section`
  padding: 0.25rem 0;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 1.1rem;
  color: ${axiomTheme.color.text.faint};
  font-size: 0.66rem;
  font-weight: 850;
  text-transform: uppercase;
  letter-spacing: 0.14em;
`;

const ResultItem = styled.button<{ $selected?: boolean }>`
  width: calc(100% - 1rem);
  margin: 0.12rem 0.5rem;
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.72rem 0.75rem;
  border: 1px solid ${props => props.$selected ? axiomTheme.color.border.focus : 'transparent'};
  border-radius: ${axiomTheme.radius.lg};
  background: ${props => props.$selected ? 'rgba(14, 116, 144, 0.2)' : 'transparent'};
  color: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${props => props.$selected ? 'rgba(14, 116, 144, 0.24)' : 'rgba(148, 163, 184, 0.08)'};
  }
`;

const ResultIcon = styled.div<{ $tone?: 'cyan' | 'emerald' | 'amber' | 'violet' | 'slate' }>`
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${axiomTheme.radius.md};
  border: 1px solid ${props => {
    switch (props.$tone) {
      case 'emerald': return 'rgba(52, 211, 153, 0.22)';
      case 'amber': return 'rgba(251, 191, 36, 0.22)';
      case 'violet': return 'rgba(167, 139, 250, 0.22)';
      case 'cyan': return 'rgba(56, 189, 248, 0.22)';
      default: return 'rgba(148, 163, 184, 0.18)';
    }
  }};
  background: rgba(15, 23, 42, 0.58);
  color: ${props => {
    switch (props.$tone) {
      case 'emerald': return axiomTheme.color.tone.emerald;
      case 'amber': return axiomTheme.color.tone.amber;
      case 'violet': return axiomTheme.color.tone.violet;
      case 'cyan': return axiomTheme.color.tone.cyan;
      default: return axiomTheme.color.text.muted;
    }
  }};
`;

const ResultText = styled.div`
  min-width: 0;
`;

const ResultTitle = styled.div`
  color: ${axiomTheme.color.text.primary};
  font-size: 0.84rem;
  font-weight: 800;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ResultSubtitle = styled.div`
  margin-top: 0.18rem;
  color: ${axiomTheme.color.text.muted};
  font-size: 0.72rem;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ResultMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: ${axiomTheme.color.text.faint};
`;

const Badge = styled.span`
  max-width: 120px;
  display: inline-flex;
  align-items: center;
  padding: 0.16rem 0.44rem;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: ${axiomTheme.radius.pill};
  background: rgba(2, 6, 23, 0.48);
  color: ${axiomTheme.color.text.muted};
  font-size: 0.63rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EmptyState = styled.div`
  padding: 2.3rem 1.25rem;
  text-align: center;
  color: ${axiomTheme.color.text.muted};
`;

const EmptyTitle = styled.div`
  color: ${axiomTheme.color.text.primary};
  font-size: 0.9rem;
  font-weight: 850;
  margin-bottom: 0.35rem;
`;

const ExampleGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.45rem;
  margin-top: 1rem;
`;

const ExampleButton = styled.button`
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: ${axiomTheme.radius.pill};
  background: rgba(15, 23, 42, 0.72);
  color: ${axiomTheme.color.text.secondary};
  padding: 0.38rem 0.7rem;
  font-size: 0.7rem;
  font-weight: 800;
  cursor: pointer;

  &:hover {
    color: ${axiomTheme.color.text.primary};
    border-color: ${axiomTheme.color.border.focus};
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1.1rem;
  border-top: 1px solid ${axiomTheme.color.border.subtle};
  color: ${axiomTheme.color.text.faint};
  font-size: 0.68rem;
`;

const FooterHints = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.45rem;
`;

const ShortcutsButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.38rem;
  border: 0;
  background: transparent;
  color: ${axiomTheme.color.text.muted};
  font-size: 0.7rem;
  font-weight: 800;
  cursor: pointer;

  &:hover {
    color: ${axiomTheme.color.text.primary};
  }
`;

const commandTone = (group: string): 'cyan' | 'emerald' | 'amber' | 'violet' | 'slate' => {
  switch (group) {
    case 'Trade Entry': return 'emerald';
    case 'Workflow': return 'amber';
    case 'Search': return 'violet';
    case 'Navigation': return 'cyan';
    default: return 'slate';
  }
};

const searchTone = (type: string): 'cyan' | 'emerald' | 'amber' | 'violet' | 'slate' => {
  switch (type) {
    case 'portfolio': return 'emerald';
    case 'trade': return 'amber';
    case 'position': return 'violet';
    case 'security': return 'cyan';
    case 'abf': return 'emerald';
    case 'abf-agreement': return 'amber';
    case 'pc': return 'violet';
    case 'll': return 'cyan';
    case 'loan-document': return 'amber';
    default: return 'slate';
  }
};

const groupSearchResults = (results: SearchResult[]) =>
  results.reduce((acc, result) => {
    const key = result.type || 'result';
    if (!acc[key]) acc[key] = [];
    acc[key].push(result);
    return acc;
  }, {} as Record<string, SearchResult[]>);

const resultTypeLabels: Record<string, string> = {
  security: 'Securities',
  portfolio: 'Portfolios',
  trade: 'Trades',
  position: 'Positions',
  loan: 'Loans',
  clo: 'CLO',
  cmbs: 'CMBS',
  cds: 'CDS',
  fund: 'Funds',
  abf: 'ABF facilities',
  'abf-agreement': 'ABF agreements awaiting activation',
  pc: 'Private credit facilities',
  ll: 'Leveraged loans',
  'loan-document': 'Loan documents to review or import',
};

export const QuickSearch: React.FC<QuickSearchProps> = ({ isOpen, onClose, onShowShortcuts }) => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const commandMatches = useMemo(
    () => getOperatorCommandMatches(query, query.trim() ? 7 : 8),
    [query]
  );

  const searchItems = useMemo(
    () => results.map(result => ({
      kind: 'search' as const,
      id: `search-${result.type}-${result.id}`,
      result,
    })),
    [results]
  );

  const commandItems = useMemo(
    () => commandMatches.map(command => ({
      kind: 'command' as const,
      id: `command-${command.id}`,
      command,
    })),
    [commandMatches]
  );

  const flatItems: PaletteItem[] = useMemo(
    () => [...commandItems, ...searchItems],
    [commandItems, searchItems]
  );

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    if (selectedIndex > flatItems.length - 1) {
      setSelectedIndex(Math.max(flatItems.length - 1, 0));
    }
  }, [flatItems.length, selectedIndex]);

  const performSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`/api/search/global?query=${encodeURIComponent(searchQuery)}&limit=24`);
      const searchResults: SearchResult[] = Array.isArray(res.data)
        ? res.data.map((it: any) => ({
            id: it.id,
            type: it.type,
            assetClass: it.assetClass,
            title: it.title || 'Unknown',
            subtitle: [it.assetClass, it.subtitle].filter(Boolean).join(' - '),
            route: it.route,
            metadata: it.metadata,
          }))
        : [];
      setResults(searchResults);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(query.trim());
    }, 220);

    return () => clearTimeout(timer);
  }, [query, performSearch]);

  const executeCommand = useCallback((command: OperatorCommandMatch) => {
    onClose();
    if (command.sessionPrefill) {
      sessionStorage.setItem('pendingTradeData', JSON.stringify(command.sessionPrefill));
    }
    navigate(command.route, command.state ? { state: command.state } : undefined);
  }, [navigate, onClose]);

  const executeSearchResult = useCallback((result: SearchResult) => {
    onClose();
    if (result.route) {
      navigate(result.route);
    }
  }, [navigate, onClose]);

  const handleSelect = useCallback((item: PaletteItem | undefined) => {
    if (!item) return;
    if (item.kind === 'command') {
      executeCommand(item.command);
    } else {
      executeSearchResult(item.result);
    }
  }, [executeCommand, executeSearchResult]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex(prev => flatItems.length === 0 ? 0 : Math.min(prev + 1, flatItems.length - 1));
          break;
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          event.preventDefault();
          handleSelect(flatItems[selectedIndex]);
          break;
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [flatItems, handleSelect, isOpen, onClose, selectedIndex]);

  const groupedSearchResults = useMemo(() => groupSearchResults(results), [results]);

  const globalIndexForSearchResult = (result: SearchResult) =>
    commandItems.length + searchItems.findIndex(item => item.result === result);

  const examples = ['FI', 'IRS', 'POS VANGUARD', 'RFQ IBM 2034', 'TKT FI IBM 5MM BUY', 'COMP BREACHES'];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <Overlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <Palette
          initial={{ opacity: 0, y: -18, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -18, scale: 0.98 }}
          transition={{ duration: 0.16, ease: 'easeOut' }}
          onClick={event => event.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label="Command palette"
        >
          <SearchHeader>
            <HeaderIcon>
              <TerminalSquare size={18} />
            </HeaderIcon>
            <SearchInput
              ref={inputRef}
              type="text"
              placeholder="Type FI, IRS, POS VANGUARD, TKT FI IBM 5MM BUY, or search all assets"
              value={query}
              onChange={event => setQuery(event.target.value)}
              aria-label="Command or search"
            />
            <KeyHint>ESC</KeyHint>
          </SearchHeader>

          <ResultsContainer>
            {commandItems.length > 0 && (
              <ResultSection>
                <SectionTitle>
                  <Command size={13} />
                  Commands
                </SectionTitle>
                {commandItems.map((item, index) => (
                  <ResultItem
                    key={item.id}
                    $selected={index === selectedIndex}
                    onClick={() => executeCommand(item.command)}
                  >
                    <ResultIcon $tone={commandTone(item.command.group)}>
                      <ArrowRight size={16} />
                    </ResultIcon>
                    <ResultText>
                      <ResultTitle>{item.command.title}</ResultTitle>
                      <ResultSubtitle>
                        {item.command.subtitle}
                        {item.command.example ? ` - ${item.command.example}` : ''}
                      </ResultSubtitle>
                    </ResultText>
                    <ResultMeta>
                      <Badge>{item.command.group}</Badge>
                      {item.command.shortcut && <KeyHint>{item.command.shortcut}</KeyHint>}
                    </ResultMeta>
                  </ResultItem>
                ))}
              </ResultSection>
            )}

            {loading && (
              <EmptyState>
                <Loader2 size={22} style={{ margin: '0 auto 0.55rem' }} />
                Searching across securities, portfolios, trades, and positions
              </EmptyState>
            )}

            {!loading && results.length > 0 && (
              Object.entries(groupedSearchResults).map(([type, typeResults]) => (
                <ResultSection key={type}>
                  <SectionTitle>
                    <FileSearch size={13} />
                    {resultTypeLabels[type] || type}
                  </SectionTitle>
                  {typeResults.map(result => {
                    const globalIndex = globalIndexForSearchResult(result);
                    return (
                      <ResultItem
                        key={`${result.type}-${result.id}`}
                        $selected={globalIndex === selectedIndex}
                        onClick={() => executeSearchResult(result)}
                      >
                        <ResultIcon $tone={searchTone(result.type)}>
                          <Search size={16} />
                        </ResultIcon>
                        <ResultText>
                          <ResultTitle>{result.title}</ResultTitle>
                          <ResultSubtitle>{result.subtitle}</ResultSubtitle>
                        </ResultText>
                        <ResultMeta>
                          {result.assetClass && <Badge>{result.assetClass}</Badge>}
                          <ArrowRight size={14} />
                        </ResultMeta>
                      </ResultItem>
                    );
                  })}
                </ResultSection>
              ))
            )}

            {!loading && query.trim().length >= 2 && flatItems.length === 0 && (
              <EmptyState>
                <EmptyTitle>No command or result found</EmptyTitle>
                Try a Bloomberg-style mnemonic, issuer, portfolio, CUSIP, or ticket number.
              </EmptyState>
            )}

            {!loading && query.trim().length < 2 && (
              <EmptyState>
                <EmptyTitle>Command-first search</EmptyTitle>
                Use mnemonics for speed, or type any borrower, deal, fund, property, ticker, or CUSIP.
                <ExampleGrid>
                  {examples.map(example => (
                    <ExampleButton
                      key={example}
                      type="button"
                      onClick={() => setQuery(example)}
                    >
                      {example}
                    </ExampleButton>
                  ))}
                </ExampleGrid>
              </EmptyState>
            )}
          </ResultsContainer>

          <Footer>
            <FooterHints>
              <KeyHint>Up/Down</KeyHint> Navigate
              <KeyHint>Enter</KeyHint> Select
              <KeyHint>Esc</KeyHint> Close
            </FooterHints>
            {onShowShortcuts && (
              <ShortcutsButton
                type="button"
                onClick={() => {
                  onClose();
                  onShowShortcuts();
                }}
              >
                <Keyboard size={13} />
                Shortcuts
              </ShortcutsButton>
            )}
          </Footer>
        </Palette>
      </Overlay>
    </AnimatePresence>
  );
};

export default QuickSearch;
